import type {
  GalleryEditRequest,
  GalleryImageViewRequest,
} from './editorjs-gallery-tool';
import type { EditorJsData } from './editorjs-types';

import { Text } from '@chakra-ui/react';
import {
  forwardRef,
  Suspense,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import './editor.scss';
import { DocumentFilesBlock } from './document-files-block';
import { useDocumentMermaidVisibility } from './document-mermaid-visibility-context';
import {
  normalizeDocumentLinksInEditorJsData,
  rewriteEditorAnchorHrefIfDocumentPath,
} from './editor-document-links';
import AddToChatTune from './editorjs-add-to-chat-tune';
import AiInstructionsTune, {
  type AiInstructionsEditRequest,
} from './editorjs-ai-instructions-tune';
import { useGalleryImageModal } from './modals/gallery-image-modal-context';
import { SubpagesBlock } from './subpages-block.tsx';

import { useAppSettings } from '#src/providers/app-settings-provider';

export interface EditorJsEditorHandle {
  getValue: () => EditorJsData | null;
  resetContent: (value: EditorJsData | null) => void;
  /** Flush Editor.js internal state into `latestValueRef` (same as `onChange`). Call before persist. */
  flushSave: () => Promise<void>;
}

/** Values read at call time by Editor.js tools so the instance is not recreated when props/context change. */
type EditorDynamicConfig = {
  onUpload?: (file: File) => Promise<string>;
  onAddToChat?: (blockId: string) => void;
  isSourceVisible: (entryId: string) => boolean;
  setSourceVisible: (entryId: string, visible: boolean) => void;
  onRequestGalleryEdit?: (request: GalleryEditRequest) => void;
  onOpenGalleryImage?: (request: GalleryImageViewRequest) => void;
  onRequestAiInstructionsEdit?: (request: AiInstructionsEditRequest) => void;
};

interface EditDocumentEditorProps {
  /** Initial content as Editor.js data. */
  value?: EditorJsData | null;
  /** Document id for AI processing prompts (undefined for new/unsaved docs). */
  documentId?: string;
  /** API origin for upload links in the Files block (e.g. `https://api.example.com`). */
  apiHost: string;
  /** Signed-in user email (Files block upload modal). */
  userEmail: string;
  /** Whether this is a new document (not yet saved). */
  isNewDocument: boolean;
  /** Dim editor body text until the user changes content (e.g. unsaved path). */
  editorPlaceholderMuted?: boolean;
  /** Handler for image uploads (drag-drop, paste, or button). Returns the URL. */
  onUpload?: (file: File) => Promise<string>;
  /** Current document path for context-aware tools like Subpages. */
  parentPath: string;
  /** Current child documents for the Subpages block. */
  children: Array<{ id: string; path: string; label?: string }>;
  /** Open a React-powered prompt to create a subpage. */
  onCreateSubpage?: () => void;
  /** Navigate to a document path (client-side). Used by Subpages block links. */
  onNavigateToDocument?: (path: string) => void;
  /** Add a block reference to the document chat composer (block tune). */
  onAddToChat?: (blockId: string) => void;
  /** Open React UI to edit a gallery block as JSON (block settings). */
  onRequestGalleryEdit?: (request: GalleryEditRequest) => void;
  /**
   * Open full-size gallery image preview (from `GalleryImageModalProvider` /
   * `useGalleryImageModal`).
   */
  onOpenGalleryImage?: (request: GalleryImageViewRequest) => void;
  /** Open React UI to edit per-block AI instructions (block tune). */
  onRequestAiInstructionsEdit?: (request: AiInstructionsEditRequest) => void;
}

export const EditDocumentEditor = forwardRef<
  EditorJsEditorHandle,
  EditDocumentEditorProps
>(function EditDocumentEditor(
  {
    value,
    documentId,
    apiHost,
    userEmail,
    onUpload,
    parentPath,
    children,
    onCreateSubpage,
    onNavigateToDocument,
    onAddToChat,
    onRequestGalleryEdit,
    onOpenGalleryImage,
    onRequestAiInstructionsEdit,
    editorPlaceholderMuted = false,
  },
  ref,
) {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<any | null>(null);
  /** Serializes `api.saver.save()` / `editor.save()` so out-of-order completions cannot clobber `latestValueRef`. */
  const persistChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const latestValueRef = useRef<EditorJsData | null>(value ?? null);
  const navigate = useNavigate();
  const { documentPathRegex } = useAppSettings();
  const { isSourceVisible, setSourceVisible } = useDocumentMermaidVisibility();
  const openGalleryImageFromContext = useGalleryImageModal();

  const dynamicConfigRef = useRef<EditorDynamicConfig>({
    onUpload,
    onAddToChat,
    isSourceVisible,
    setSourceVisible,
    onRequestGalleryEdit,
    onOpenGalleryImage,
    onRequestAiInstructionsEdit,
  });
  dynamicConfigRef.current = {
    onUpload,
    onAddToChat,
    isSourceVisible,
    setSourceVisible,
    onRequestGalleryEdit,
    onOpenGalleryImage: onOpenGalleryImage ?? openGalleryImageFromContext,
    onRequestAiInstructionsEdit,
  };

  const commitEditorOutput = useCallback(
    (data: EditorJsData) => {
      const origin =
        typeof window !== 'undefined' ? window.location.origin : '';
      latestValueRef.current =
        origin.length > 0
          ? normalizeDocumentLinksInEditorJsData(
              data,
              documentPathRegex,
              origin,
            )
          : data;
    },
    [documentPathRegex],
  );

  const schedulePersist = useCallback((run: () => Promise<void>) => {
    const next = persistChainRef.current.then(run).catch(() => undefined);
    persistChainRef.current = next;
    return next;
  }, []);

  const [portalTargets, setPortalTargets] = useState<Map<string, HTMLElement>>(
    () => new Map(),
  );

  const registerPortalTarget = useCallback((id: string, el: HTMLElement) => {
    setPortalTargets((prev) => {
      const next = new Map(prev);
      next.set(id, el);
      return next;
    });
  }, []);

  const unregisterPortalTarget = useCallback((id: string) => {
    setPortalTargets((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const [filesPortalTargets, setFilesPortalTargets] = useState<
    Map<string, HTMLElement>
  >(() => new Map());

  const registerFilesPortalTarget = useCallback(
    (id: string, el: HTMLElement) => {
      setFilesPortalTargets((prev) => {
        const next = new Map(prev);
        next.set(id, el);
        return next;
      });
    },
    [],
  );

  const unregisterFilesPortalTarget = useCallback((id: string) => {
    setFilesPortalTargets((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const subpagesPortalNodes = useMemo(() => {
    return Array.from(portalTargets.entries()).map(([id, el]) =>
      createPortal(
        <SubpagesBlock
          key={id}
          parentPath={parentPath}
          children={children}
          onCreateSubpage={onCreateSubpage}
          onNavigateToDocument={onNavigateToDocument}
        />,
        el,
      ),
    );
  }, [
    portalTargets,
    parentPath,
    children,
    onCreateSubpage,
    onNavigateToDocument,
  ]);

  const filesPortalNodes = useMemo(() => {
    return Array.from(filesPortalTargets.entries()).map(([id, el]) =>
      createPortal(
        <Suspense
          key={id}
          fallback={
            <Text color="fg.muted" fontSize="sm">
              Loading uploads…
            </Text>
          }
        >
          <DocumentFilesBlock
            documentId={documentId}
            apiHost={apiHost}
            userEmail={userEmail}
            documentPath={parentPath}
          />
        </Suspense>,
        el,
      ),
    );
  }, [apiHost, documentId, filesPortalTargets, parentPath, userEmail]);

  useEffect(() => {
    let cancelled = false;

    const setup = async () => {
      if (!holderRef.current) return;

      try {
        const [
          { default: EditorJS },
          { default: CodeTool },
          HeaderModule,
          { default: ImageTool },
          { default: EditorjsList },
          { default: TableTool },
          { SubpagesTool },
          { default: MermaidTool },
          { GalleryTool },
          { FilesTool },
          StrikethroughModule,
        ] = await Promise.all([
          import('@editorjs/editorjs'),
          import('@editorjs/code'),
          import('@editorjs/header'),
          import('@editorjs/image'),
          import('@editorjs/list'),
          import('@editorjs/table'),
          import('./editorjs-subpages-tool'),
          import('./editorjs-mermaid-tool'),
          import('./editorjs-gallery-tool'),
          import('./editorjs-files-tool'),
          import('editorjs-strikethrough'),
        ]);

        if (cancelled || !holderRef.current) return;

        const hasUploader = Boolean(dynamicConfigRef.current.onUpload);

        const headerClass =
          typeof HeaderModule === 'function'
            ? HeaderModule
            : ((HeaderModule as any).default ?? HeaderModule);

        const StrikethroughTool =
          (StrikethroughModule as any)?.default ?? StrikethroughModule;

        // Editor.js strikethrough wraps the provided `Range`. In lists, users
        // often double-click selecting a whole line (including whitespace/
        // newlines). Wrapping those edges can make Editor.js interpret it as
        // additional (empty) list items.
        //
        // Fix: build a new `Range` that starts at the first non-whitespace
        // text node within the original range and ends at the last non-
        // whitespace text node within the original range.
        const trimRangeEdges = (range: Range): Range | null => {
          const ancestor = range.commonAncestorContainer;
          const walkerRoot =
            ancestor.nodeType === 3 ? ancestor.parentNode : ancestor;
          if (!walkerRoot) return null;

          const treeWalker = document.createTreeWalker(
            walkerRoot,
            NodeFilter.SHOW_TEXT,
          );

          const intersectingNonWhitespaceTextNodes: Text[] = [];
          let node = treeWalker.nextNode() as Node | null;
          while (node) {
            const textNode = node as Text;
            if (range.intersectsNode(textNode) && textNode.data?.trim()) {
              intersectingNonWhitespaceTextNodes.push(textNode);
            }
            node = treeWalker.nextNode() as Node | null;
          }

          if (intersectingNonWhitespaceTextNodes.length === 0) return null;

          const first = intersectingNonWhitespaceTextNodes[0];
          const last =
            intersectingNonWhitespaceTextNodes[
              intersectingNonWhitespaceTextNodes.length - 1
            ];

          const firstStartLimit =
            range.startContainer === first ? range.startOffset : 0;
          const lastEndLimit =
            range.endContainer === last ? range.endOffset : last.data.length;

          // Adjust start/end offsets within the first/last text nodes by
          // trimming whitespace characters.
          let startOffset = Math.min(firstStartLimit, first.data.length);
          while (
            startOffset < first.data.length &&
            /\s/.test(first.data[startOffset] ?? '')
          ) {
            startOffset += 1;
          }

          let endOffset = Math.max(0, Math.min(lastEndLimit, last.data.length));
          while (
            endOffset > startOffset &&
            /\s/.test(last.data[endOffset - 1] ?? '')
          ) {
            endOffset -= 1;
          }

          if (endOffset <= startOffset) return null;

          const trimmed = document.createRange();
          trimmed.setStart(first, startOffset);
          trimmed.setEnd(last, endOffset);

          return trimmed.toString().trim() ? trimmed : null;
        };

        class SafeStrikethroughTool extends StrikethroughTool {
          surround(range: Range) {
            const trimmed = trimRangeEdges(range);
            if (!trimmed) return;
            return super.surround(trimmed);
          }
        }

        const inlineToolbarTools: string[] = [
          'link',
          'bold',
          'italic',
          // Editor.js strikethrough tool (wraps with <del class="cdx-strikethrough">).
          'strikethrough',
        ];

        const editor = new EditorJS({
          holder: holderRef.current,
          data: (latestValueRef.current as any) ?? { blocks: [] },
          autofocus: true,
          tools: {
            header: {
              class: headerClass,
              // Enables inline formatting toolbar on double-click/selection.
              inlineToolbar: inlineToolbarTools,
              config: {
                levels: [1, 2, 3],
                defaultLevel: 2,
              },
            },
            list: {
              class: EditorjsList,
              inlineToolbar: inlineToolbarTools,
            },
            table: {
              // @editorjs/table typings require `config` on constructor; Editor.js passes optional config.
              class: TableTool as any,
              inlineToolbar: inlineToolbarTools,
            },
            strikethrough: {
              class: SafeStrikethroughTool as any,
            },
            code: CodeTool,
            image: {
              class: ImageTool,
              ...(hasUploader
                ? {
                    config: {
                      uploader: {
                        async uploadByFile(file: File) {
                          const upload = dynamicConfigRef.current.onUpload;
                          if (!upload) {
                            return { success: 0 };
                          }
                          const url = await upload(file);
                          return {
                            success: 1,
                            file: {
                              url,
                            },
                          };
                        },
                      },
                    },
                  }
                : {}),
            },
            mermaid: {
              class: MermaidTool,
              config: {
                getShowSource: (entryId: string) =>
                  dynamicConfigRef.current.isSourceVisible(entryId),
                setShowSource: (entryId: string, visible: boolean) =>
                  dynamicConfigRef.current.setSourceVisible(entryId, visible),
              },
            },
            subpages: {
              class: SubpagesTool,
              config: {
                registerPortalTarget,
                unregisterPortalTarget,
              },
            },
            files: {
              class: FilesTool,
              config: {
                registerFilesPortalTarget,
                unregisterFilesPortalTarget,
              },
            },
            gallery: {
              class: GalleryTool,
              config: {
                onRequestGalleryEdit: (request: GalleryEditRequest) => {
                  dynamicConfigRef.current.onRequestGalleryEdit?.(request);
                },
                onOpenGalleryImage: (request: GalleryImageViewRequest) => {
                  dynamicConfigRef.current.onOpenGalleryImage?.(request);
                },
              },
            },
            addToChatTune: {
              class: AddToChatTune,
              config: {
                onAddToChatRequested: (blockId: string) => {
                  dynamicConfigRef.current.onAddToChat?.(blockId);
                },
              },
            },
            aiInstructions: {
              class: AiInstructionsTune,
              config: {
                onRequestAiInstructionsEdit: (
                  request: AiInstructionsEditRequest,
                ) => {
                  dynamicConfigRef.current.onRequestAiInstructionsEdit?.(
                    request,
                  );
                },
              },
            },
          },
          tunes: ['addToChatTune', 'aiInstructions'],
          onChange(api) {
            void schedulePersist(async () => {
              const data = (await api.saver.save()) as EditorJsData;
              commitEditorOutput(data);
            });
          },
        });

        editorRef.current = editor;
      } catch {
        if (holderRef.current) {
          holderRef.current.innerText =
            'Failed to load editor. Check console for details.';
        }
      }
    };

    void setup();

    return () => {
      cancelled = true;
      persistChainRef.current = Promise.resolve();
      const editor = editorRef.current;
      if (editor) {
        void editor.isReady
          .then(() => {
            editor.destroy();
            editorRef.current = null;
          })
          .catch(() => {
            editorRef.current = null;
          });
      }
      setPortalTargets(new Map());
      setFilesPortalTargets(new Map());
    };
  }, [
    commitEditorOutput,
    documentPathRegex,
    registerFilesPortalTarget,
    registerPortalTarget,
    schedulePersist,
    unregisterFilesPortalTarget,
    unregisterPortalTarget,
  ]);

  // Plain click on <a href>: Editor.js does not follow links without Ctrl/Cmd.
  // `rewriteEditorAnchorHrefIfDocumentPath` only rewrites short document paths;
  // it returns null for reserved prefixes like `/documents/...`, so we still
  // navigate those hrefs here. Same-origin `/api/...` links are left to the
  // browser so uploads and other API URLs are not routed as app pages.
  useEffect(() => {
    const el = holderRef.current;
    if (!el) return;
    const origin = window.location.origin;
    const onClickCapture = (ev: MouseEvent) => {
      if (ev.defaultPrevented) return;
      if (ev.button !== 0) return;
      if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      const t = ev.target;
      if (!(t instanceof Element)) return;
      const anchor = t.closest('a');
      if (!anchor || !el.contains(anchor)) return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      const trimmed = href.trim();
      if (trimmed.toLowerCase().startsWith('javascript:')) return;

      const shortDocDest = rewriteEditorAnchorHrefIfDocumentPath(
        href,
        documentPathRegex,
        origin,
      );
      if (shortDocDest) {
        ev.preventDefault();
        ev.stopPropagation();
        navigate(shortDocDest);
        return;
      }

      try {
        if (/^mailto:/i.test(trimmed) || /^tel:/i.test(trimmed)) {
          ev.preventDefault();
          ev.stopPropagation();
          window.location.assign(trimmed);
          return;
        }
        if (/^https?:\/\//i.test(trimmed)) {
          const u = new URL(trimmed);
          if (u.origin === origin) {
            // API routes (uploads, GraphQL, etc.) are not SPA pages; use full navigation.
            if (u.pathname === '/api' || u.pathname.startsWith('/api/')) {
              return;
            }
            ev.preventDefault();
            ev.stopPropagation();
            navigate(`${u.pathname}${u.search}${u.hash}`);
          } else {
            ev.preventDefault();
            ev.stopPropagation();
            window.open(u.href, '_blank', 'noopener,noreferrer');
          }
          return;
        }
        if (trimmed.startsWith('/')) {
          const u = new URL(trimmed, `${origin}/`);
          if (u.origin === origin) {
            if (u.pathname === '/api' || u.pathname.startsWith('/api/')) {
              return;
            }
            ev.preventDefault();
            ev.stopPropagation();
            navigate(`${u.pathname}${u.search}${u.hash}`);
          }
        }
      } catch {
        /* ignore invalid href */
      }
    };
    el.addEventListener('click', onClickCapture, true);
    return () => el.removeEventListener('click', onClickCapture, true);
  }, [documentPathRegex, navigate]);

  // When the `value` prop changes (e.g. new document loaded), re-render content
  useEffect(() => {
    if (!editorRef.current) return;
    persistChainRef.current = Promise.resolve();
    latestValueRef.current = value ?? { blocks: [] };
    void editorRef.current.isReady
      .then(() =>
        editorRef.current.render(
          (latestValueRef.current as any) ?? { blocks: [] },
        ),
      )
      .catch(() => {});
  }, [value]);

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => latestValueRef.current,
      resetContent: (data: EditorJsData | null) => {
        persistChainRef.current = Promise.resolve();
        latestValueRef.current = data ?? { blocks: [] };
        const editor = editorRef.current;
        if (!editor) return;
        void editor.isReady
          .then(() =>
            editor.render((latestValueRef.current as any) ?? { blocks: [] }),
          )
          .catch(() => {});
      },
      flushSave: async () => {
        const editor = editorRef.current;
        if (!editor) return;
        await schedulePersist(async () => {
          const data = (await editor.save()) as EditorJsData;
          commitEditorOutput(data);
        });
      },
    }),
    [commitEditorOutput, schedulePersist],
  );

  return (
    <div
      className={
        editorPlaceholderMuted
          ? 'edit-document-editor-root edit-document-editor-root--new-placeholder-muted'
          : 'edit-document-editor-root'
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minHeight: '300px',
      }}
    >
      {subpagesPortalNodes}
      {filesPortalNodes}
      <div
        style={{
          flex: 1,
          minHeight: '300px',
          width: '100%',
        }}
      >
        <div
          ref={holderRef}
          style={{
            height: '100%',
            width: '100%',
            minHeight: '300px',
          }}
          className="editorjs-root editorjs-holder"
        />
      </div>
    </div>
  );
});
